import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { LogOut, Menu, X, ShieldCheck, LayoutDashboard } from "lucide-react";
import RichTextRenderer from "./RichTextRenderer";
import { api } from "../lib/api";
import HeaderOnboarding from "./HeaderOnboarding";
import { getPublicPath } from "../utils/seoPages";

const LOGO = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654890/sf-learning/brand/logo_sf_learning.png";

const EMPTY_COUNTS = {
  courses: 0,
  blog: 0,
  community: 0,
  messages: 0,
  dashboard: 0,
  affiliate: 0,
  admin: 0,
  total: 0,
};

function formatBadgeCount(value) {
  const count = Number(value || 0);
  if (count <= 0) return "";
  return count > 9 ? "9+" : String(count);
}

function MenuBadge({ count }) {
  const label = formatBadgeCount(count);
  if (!label) return null;

  return (
    <span
      className="ml-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-orange px-1.5 text-[0.65rem] font-black leading-none text-white shadow-sm"
      aria-label={`${count} notification${Number(count) > 1 ? "s" : ""}`}
    >
      {label}
    </span>
  );
}

function MobileMenuBadge({ count }) {
  const label = formatBadgeCount(count);
  if (!label) return null;

  return (
    <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-orange px-1.5 text-[0.65rem] font-black leading-none text-white">
      {label}
    </span>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings: s } = useSettings();
  const [open, setOpen] = React.useState(false);
  const [menuCounts, setMenuCounts] = React.useState(EMPTY_COUNTS);
  const navigate = useNavigate();
  const canAccessMessages = user?.role === "admin" || user?.has_active_formation === true;
  const catalogPath = getPublicPath(s, "/catalog");
  const blogPath = getPublicPath(s, "/blog");
  const affiliatePath = getPublicPath(s, "/affiliate");
  const mentionsPath = getPublicPath(s, "/mentions-legales");
  const privacyPath = getPublicPath(s, "/politique-confidentialite");
  const cgvPath = getPublicPath(s, "/conditions-generales-de-vente");

  const refreshMenuCounts = React.useCallback(async () => {
    if (!user) {
      setMenuCounts(EMPTY_COUNTS);
      return;
    }

    try {
      const { data } = await api.get("/notifications/menu-counts");
      setMenuCounts({ ...EMPTY_COUNTS, ...(data || {}) });
    } catch (err) {
      setMenuCounts(EMPTY_COUNTS);
    }
  }, [user]);

  React.useEffect(() => {
    refreshMenuCounts();

    if (!user) return undefined;

    const interval = window.setInterval(refreshMenuCounts, 30000);
    const handleFocus = () => refreshMenuCounts();
    const handleMenuRefresh = () => refreshMenuCounts();
    window.addEventListener("focus", handleFocus);
    window.addEventListener("sf-menu-counts-refresh", handleMenuRefresh);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("sf-menu-counts-refresh", handleMenuRefresh);
    };
  }, [user, refreshMenuCounts]);

  const markMenuSectionRead = React.useCallback((section) => {
    if (!user || !section) return;

    setMenuCounts((prev) => {
      const next = { ...prev, [section]: 0 };
      next.total = Math.max(
        0,
        Number(next.courses || 0) +
          Number(next.blog || 0) +
          Number(next.community || 0) +
          Number(next.messages || 0) +
          Number(next.dashboard || 0) +
          Number(next.affiliate || 0) +
          Number(next.admin || 0)
      );
      return next;
    });

    api.patch(`/notifications/menu-counts/${section}/read`).catch(() => {
      refreshMenuCounts();
    });
  }, [user, refreshMenuCounts]);

  const handleLogout = async () => {
    await logout();
    setMenuCounts(EMPTY_COUNTS);
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur bg-brand-mint/85 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[76px] flex items-center justify-between gap-4">
          <Link to="/" className="flex min-w-0 shrink-0 items-center gap-3" data-testid="logo-link">
            <img src={LOGO} alt={s.site_name} className="h-12 w-12 shrink-0 rounded-lg" />
            <div className="min-w-0 leading-none">
              <div className="font-heading font-black text-[1rem] sm:text-[1.1rem] tracking-tight text-black whitespace-nowrap">
                {(s.site_name || "SF LEARNING").toUpperCase()}
              </div>
              <div className="max-w-[180px] truncate whitespace-nowrap text-[0.65rem] sm:max-w-[260px] sm:text-[0.7rem] text-gray-500 tracking-wider uppercase">
                {s.tagline}
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-7 text-sm font-medium">
            <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="nav-home">
              Accueil
            </NavLink>
            <NavLink to={catalogPath} onClick={() => markMenuSectionRead("courses")} className={({ isActive }) => `nav-link inline-flex items-center ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="nav-catalog">
              Formations <MenuBadge count={menuCounts.courses} />
            </NavLink>
            <NavLink to={blogPath} onClick={() => markMenuSectionRead("blog")} className={({ isActive }) => `nav-link inline-flex items-center ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="nav-blog">
              Blog <MenuBadge count={menuCounts.blog} />
            </NavLink>
            <NavLink to="/community" onClick={() => markMenuSectionRead("community")} className={({ isActive }) => `nav-link inline-flex items-center ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="nav-community">
              Communauté <MenuBadge count={menuCounts.community} />
            </NavLink>
            {canAccessMessages && (
              <NavLink to="/messages" onClick={() => markMenuSectionRead("messages")} className={({ isActive }) => `nav-link inline-flex items-center ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="messages-link">
                Messages <MenuBadge count={menuCounts.messages} />
              </NavLink>
            )}
            <NavLink to={affiliatePath} onClick={() => markMenuSectionRead("affiliate")} className={({ isActive }) => `nav-link inline-flex items-center ${isActive ? "active text-brand-green" : "text-black/80 hover:text-black"}`} data-testid="nav-affiliate">
              Parrainage <MenuBadge count={menuCounts.affiliate} />
            </NavLink>
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-ghost relative text-sm !font-medium" data-testid="admin-link">
                    <ShieldCheck className="w-4 h-4" /> Admin <MenuBadge count={menuCounts.admin} />
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => markMenuSectionRead("dashboard")} className="btn-ghost whitespace-nowrap relative text-sm !font-medium" data-testid="dashboard-link">
                  <LayoutDashboard className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">Mon espace</span> <MenuBadge count={menuCounts.dashboard} />
                </Link>
                <button onClick={handleLogout} className="btn-ghost text-sm !font-medium" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm !font-medium" data-testid="login-link">Se connecter</Link>
                <Link to="/register" className="btn-primary text-sm !font-medium" data-testid="register-link">Commencer</Link>
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2 xl:hidden">
            <button className="p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Ouvrir le menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-emerald-100 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-2">
              <Link to="/" onClick={() => setOpen(false)} className="block py-2" data-testid="mobile-nav-home">Accueil</Link>
              <Link to={catalogPath} onClick={() => { setOpen(false); markMenuSectionRead("courses"); }} className="block py-2" data-testid="mobile-nav-catalog">
                Formations <MobileMenuBadge count={menuCounts.courses} />
              </Link>
              <Link to={blogPath} onClick={() => { setOpen(false); markMenuSectionRead("blog"); }} className="block py-2" data-testid="mobile-nav-blog">
                Blog <MobileMenuBadge count={menuCounts.blog} />
              </Link>
              <Link to="/community" onClick={() => { setOpen(false); markMenuSectionRead("community"); }} className="block py-2" data-testid="mobile-nav-community">
                Communauté <MobileMenuBadge count={menuCounts.community} />
              </Link>
              <Link to={affiliatePath} onClick={() => { setOpen(false); markMenuSectionRead("affiliate"); }} className="block py-2" data-testid="mobile-nav-affiliate">
                Parrainage <MobileMenuBadge count={menuCounts.affiliate} />
              </Link>
              <hr />
              {user ? (
                <>
                  {canAccessMessages && (
                    <Link to="/messages" onClick={() => { setOpen(false); markMenuSectionRead("messages"); }} className="block py-2" data-testid="mobile-nav-messages">
                      Messages <MobileMenuBadge count={menuCounts.messages} />
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => { setOpen(false); markMenuSectionRead("dashboard"); }} className="block py-2" data-testid="mobile-nav-dashboard">
                    Mon espace <MobileMenuBadge count={menuCounts.dashboard} />
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => { setOpen(false); }} className="block py-2" data-testid="mobile-nav-admin">
                      Admin <MobileMenuBadge count={menuCounts.admin} />
                    </Link>
                  )}
                  <button onClick={() => { setOpen(false); handleLogout(); }} className="block py-2 w-full text-left">Déconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="block py-2">Se connecter</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="block py-2 font-semibold text-brand-green">Commencer</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <HeaderOnboarding user={user} mobileMenuOpen={open} openMobileMenu={() => setOpen(true)} canAccessMessages={canAccessMessages} />

      <main className="flex-1">{children}</main>

      <footer className="bg-white border-t border-emerald-100 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src={LOGO} alt={s.site_name} className="h-10 w-10 rounded-md" />
              <span className="font-heading font-black text-lg">{(s.site_name || "SF LEARNING").toUpperCase()}</span>
            </div>
            <RichTextRenderer html={s.footer_description} className="text-sm text-gray-600 mt-3 max-w-xs" />
          </div>
          <div>
            <p className="label mb-3">Plateforme</p>
            <ul className="space-y-2 text-sm">
              <li><Link to={catalogPath} className="hover:text-brand-green">Formations</Link></li>
              <li><Link to={blogPath} className="hover:text-brand-green">Blog</Link></li>
              <li><Link to="/community" className="hover:text-brand-green">Communauté</Link></li>
              <li><Link to={affiliatePath} className="hover:text-brand-green">Devenir affilié</Link></li>
            </ul>
          </div>
          <div>
            <p className="label mb-3">Compte</p>
            <ul className="space-y-2 text-sm">
              {/* Connexion / inscription n'ont plus de sens une fois connecté. */}
              {!user && <li><Link to="/login" className="hover:text-brand-green">Connexion</Link></li>}
              {!user && <li><Link to="/register" className="hover:text-brand-green">Inscription</Link></li>}
              {user && <li><Link to="/dashboard" className="hover:text-brand-green">Mon espace</Link></li>}
              {canAccessMessages && <li><Link to="/messages" className="hover:text-brand-green">Messages</Link></li>}
            </ul>
          </div>
          <div>
            <p className="label mb-3">Contact</p>
            <RichTextRenderer html={s.footer_contact_text} className="text-sm text-gray-600" />
            {s.contact_email && <p className="text-sm mt-2"><a href={`mailto:${s.contact_email}`} className="text-brand-green hover:underline">{s.contact_email}</a></p>}
            <div className="mt-4 space-y-2 text-sm">
              <Link to={mentionsPath} className="block hover:text-brand-green">Mentions légales</Link>
              <Link to={privacyPath} className="block hover:text-brand-green">Politique de confidentialité</Link>
              <Link to={cgvPath} className="block hover:text-brand-green">Contrat et conditions générales de vente</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-emerald-100 py-5 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} {s.site_name} — {s.footer_copyright}
        </div>
      </footer>
    </div>
  );
}
